(() => {
  const toolKeys = ['cone', 'boule', 'mais', 'flamme', 'mandrin', 'cuticle_cone', 'bud', 'icicle', 'truncated_cone', 'cylinder', 'new_cone', 'new_olive', 'new_tonneau'];
  const nailKeys = ['naturel', 'long', 'squoval', 'amande', 'coffin'];
  const nailModels = { naturel: 'ongle-naturel', long: 'ongle-carre-long-effile', squoval: 'ongle-squoval', amande: 'ongle-amande', coffin: 'ongle-coffin' };
  const scaleBySize = { M: 0.7, L: 0.85, XL: 1 };
  const cache = new Map();
  const confirmations = new WeakMap();
  const originalVisuals = new WeakMap();
  const dialogs = new WeakMap();
  const clamp = (value, min, max) => Math.max(min, Math.min(max, value));
  const sortedSelection = (values, allowed) => {
    if (!Array.isArray(values) || values.some(value => !allowed.includes(value)) || new Set(values).size !== values.length) throw Error('Invalid selection');
    return allowed.filter(value => values.includes(value));
  };
  function normalize(input) {
    if (!input || (input.v !== undefined && input.v !== 1) || !['essentiel', 'formation', 'outils5'].includes(input.kit) || !scaleBySize[input.size]) throw Error('Invalid kit');
    const tools = sortedSelection(input.tools, toolKeys);
    if (tools.length !== (input.kit === 'essentiel' ? 2 : 5)) throw Error('Incomplete tool selection');
    let nails = [], files = [];
    if (input.kit === 'essentiel') {
      nails = sortedSelection(input.nails, nailKeys);
      files = sortedSelection(input.files, ['droite', 'courbee']);
      if (nails.length !== 2 || files.length !== 1) throw Error('Incomplete kit');
    } else if (input.kit === 'formation') {
      nails = [...nailKeys]; files = ['droite', 'courbee'];
    }
    return { v: 1, kit: input.kit, size: input.size, nails, files, tools };
  }
  const signature = state => JSON.stringify(normalize(state));
  function stateFromForm(form) {
    const card = form.closest('[data-mrf-card]');
    const kit = card?.dataset.productCode;
    const size = form.querySelector('[data-mrf-variant]:checked')?.dataset.size;
    const tools = [...form.querySelectorAll('[data-mrf-tool-choice]:checked')].map(choice => choice.dataset.toolId);
    const nailOptions = [...form.querySelectorAll('[data-mrf-nail-choice]')];
    const nails = [...form.querySelectorAll('[data-mrf-nail-select]')].map(select => nailOptions.find(choice => choice.value === select.value)?.dataset.nailKey).filter(Boolean);
    const fileOptions = [...form.querySelectorAll('input[name="properties[Lime incluse]"]')];
    const files = fileOptions.filter(choice => choice.checked).map(choice => choice.dataset.mrfFileKey || ['droite', 'courbee'][fileOptions.indexOf(choice)]);
    return normalize({ kit, size, tools, nails, files });
  }
  function loadJSON(url) {
    if (!url) return Promise.reject(Error('Missing asset'));
    if (!cache.has(url)) {
      const task = (async () => {
        const request = async target => {
          const controller = new AbortController();
          const timeout = setTimeout(() => controller.abort(), 25000);
          try {
            const response = await fetch(target, { credentials: 'omit', signal: controller.signal });
            if (!response.ok) throw Error('Asset unavailable');
            return await response.json();
          } finally { clearTimeout(timeout); }
        };
        const data = await request(url);
        if (data?.chunked !== true) return data;
        const baseURL = new URL(url, window.location.href);
        const modelMatch = baseURL.pathname.match(/\/mrf-kit-model-([a-z0-9_-]+)\.json$/);
        if (!modelMatch || !Array.isArray(data.parts) || !data.parts.length || data.parts.length > 8 || !Number.isInteger(data.length) || data.length < 1 || data.length > 3 * 1024 * 1024) throw Error('Invalid model chunks');
        const prefix = 'mrf-kit-model-' + modelMatch[1] + '-';
        const names = new Set(); let digest, first;
        data.parts.forEach((part, index) => {
          if (typeof part !== 'string' || !part.startsWith(prefix)) throw Error('Invalid chunk filename');
          const match = part.slice(prefix.length).match(/^([a-f0-9]{10})-part-([0-9])\.json$/);
          if (!match || names.has(part)) throw Error('Invalid chunk filename');
          if (index === 0) { digest = match[1]; first = Number(match[2]); }
          if (first > 1 || match[1] !== digest || Number(match[2]) !== first + index) throw Error('Invalid chunk sequence');
          names.add(part);
        });
        const chunks = await Promise.all(data.parts.map(async part => {
          const chunk = await request(new URL(part, baseURL).href);
          if (!chunk || typeof chunk.content !== 'string' || chunk.content.length > data.length || chunk.chunked) throw Error('Invalid chunk content');
          return chunk.content;
        }));
        if (chunks.reduce((length, chunk) => length + chunk.length, 0) !== data.length) throw Error('Incomplete model chunks');
        const json = chunks.join('');
        return JSON.parse(json);
      })();
      cache.set(url, task); task.catch(() => cache.delete(url));
    }
    return cache.get(url);
  }
  function decode(value, Type) {
    if (Array.isArray(value)) return new Type(value);
    const raw = atob(value), bytes = new Uint8Array(raw.length);
    for (let index = 0; index < raw.length; index++) bytes[index] = raw.charCodeAt(index);
    return new Type(bytes.buffer);
  }
  function decodeVectors(mesh, normal = false) {
    const value = normal ? mesh.normals : mesh.positions;
    const type = normal ? mesh.normalType : mesh.positionType;
    if (type !== 'int16') return decode(value, Float32Array);
    const raw = decode(value, Int16Array), result = new Float32Array(raw.length);
    if (normal) {
      if (!(mesh.normalScale > 0)) throw Error('Invalid normal encoding');
      for (let index = 0; index < raw.length; index++) result[index] = raw[index] * mesh.normalScale;
    } else {
      if (!Array.isArray(mesh.positionScale) || !Array.isArray(mesh.positionOffset) || mesh.positionScale.length !== 3 || mesh.positionOffset.length !== 3) throw Error('Invalid position encoding');
      for (let index = 0; index < raw.length; index++) result[index] = raw[index] * mesh.positionScale[index % 3] + mesh.positionOffset[index % 3];
    }
    return result;
  }
  function modelBounds(model) {
    const bounds = model.bounds;
    if (bounds?.min?.length === 3 && bounds?.max?.length === 3) return bounds;
    if (Array.isArray(bounds) && bounds.length === 2) return { min: bounds[0], max: bounds[1] };
    throw Error('Missing model dimensions');
  }
  function groupBounds(instances, models) {
    const min = [Infinity, Infinity, Infinity], max = [-Infinity, -Infinity, -Infinity];
    for (const instance of instances) {
      const bounds = modelBounds(models[instance.id]);
      for (let axis = 0; axis < 3; axis++) {
        min[axis] = Math.min(min[axis], bounds.min[axis] + instance.position[axis]);
        max[axis] = Math.max(max[axis], bounds.max[axis] + instance.position[axis]);
      }
    }
    return { min, max, width: max[0] - min[0], depth: max[2] - min[2] };
  }
  function row(ids, models, gap = 16) {
    const result = []; let cursor = 0;
    for (const id of ids) {
      const bounds = modelBounds(models[id]);
      const width = bounds.max[0] - bounds.min[0];
      result.push({ id, position: [cursor - bounds.min[0], -bounds.min[1], -(bounds.min[2] + bounds.max[2]) / 2] });
      cursor += width + gap;
    }
    return result;
  }
  function move(instances, x, y, z) {
    return instances.map(instance => ({ ...instance, position: instance.position.map((value, axis) => value + [x, y, z][axis]) }));
  }
  async function buildScene(input, assets) {
    const state = normalize(input), stand = state.kit !== 'essentiel';
    const ids = [...state.tools, ...state.nails.map(key => nailModels[key]), ...state.files.map(key => 'lime-' + key)];
    if (state.kit !== 'outils5') ids.push('doigt');
    if (stand) ids.push('presentoir5');
    const loaded = await Promise.all([...new Set(ids)].map(async id => {
      const model = await loadJSON(assets.models[id]);
      if (model.id !== id || model.version !== 2 || model.referenceSize !== 'XL' || model.units !== 'mm' || !Array.isArray(model.meshes) || !model.meshes.length) throw Error('Incompatible model');
      return [id, model];
    }));
    const models = Object.fromEntries(loaded);
    let tools, standSpacingUnconfirmed = false;
    if (stand) {
      const manifest = await loadJSON(assets.manifest);
      const arrangement = manifest.stand?.arrangements?.[[...state.tools].sort().join('|')];
      const order = Array.isArray(arrangement) ? arrangement : arrangement?.order;
      if (!order || order.length !== 5 || [...order].sort().join(',') !== [...state.tools].sort().join(',')) throw Error('Invalid stand selection');
      if (arrangement.valid === true && arrangement.minimumConservativeRadialClearanceMM > 0) {
        const slots = manifest.stand?.slots?.map(slot => slot.positionMM);
        if (!slots || slots.length !== 5 || slots.some(position => !Array.isArray(position) || position.length !== 3 || position.some(value => !Number.isFinite(value)))) throw Error('Missing stand slots');
        tools = [{ id: 'presentoir5', position: [0, 0, 0] }, ...order.map((id, index) => ({ id, position: slots[index] }))];
      } else if (arrangement.valid === false && arrangement.fallbackLayout === 'tools_beside_stand') {
        tools = row(state.tools, models, 24);
        const toolBounds = groupBounds(tools, models), standBounds = modelBounds(models.presentoir5);
        tools.push({ id: 'presentoir5', position: [(toolBounds.min[0] + toolBounds.max[0] - standBounds.min[0] - standBounds.max[0]) / 2, -standBounds.min[1], toolBounds.max[2] - standBounds.min[2] + 32] });
        standSpacingUnconfirmed = true;
      } else throw Error('Unverified stand layout');
    } else tools = row(state.tools, models, 24);
    let instances = [...tools];
    if (state.kit !== 'outils5') {
      const nails = row(state.nails.map(key => nailModels[key]), models, 12);
      const fingerBounds = modelBounds(models.doigt);
      const finger = [{ id: 'doigt', position: [-fingerBounds.min[0], -fingerBounds.min[1], -(fingerBounds.min[2] + fingerBounds.max[2]) / 2] }];
      const fingerWidth = fingerBounds.max[0] - fingerBounds.min[0];
      const details = [...finger, ...move(nails, fingerWidth + 24, 0, 0)];
      const detailBounds = groupBounds(details, models), toolsBounds = groupBounds(tools, models);
      instances = [...details, ...move(tools, detailBounds.max[0] - toolsBounds.min[0] + 48, 0, 0)];
      const files = row(state.files.map(key => 'lime-' + key), models, 20);
      const fileBounds = groupBounds(files, models), current = groupBounds(instances, models);
      instances.push(...move(files, (current.min[0] + current.max[0] - fileBounds.width) / 2 - fileBounds.min[0], 0, current.max[2] - fileBounds.min[2] + 32));
    }
    return { state, instances, models, scale: scaleBySize[state.size], standSpacingUnconfirmed };
  }
  function createRenderer(canvas, onError = () => {}) {
    const gl = canvas.getContext('webgl', { antialias: true, alpha: true, preserveDrawingBuffer: true, powerPreference: 'low-power' });
    if (!gl) throw Error('WebGL unavailable');
    const shaderSources = [
      'attribute vec3 position;attribute vec3 normal;uniform mat3 rotation;uniform float scale;uniform float aspect;varying vec3 N;void main(){vec3 p=rotation*position;N=rotation*normal;gl_Position=vec4(p.x*scale/aspect,p.y*scale,-p.z*0.2,1.0);}',
      'precision mediump float;varying vec3 N;uniform vec3 color;void main(){vec3 n=normalize(N);if(!gl_FrontFacing)n=-n;vec3 light=normalize(vec3(-0.5,0.8,1.2));float d=max(dot(n,light),0.0);float f=max(dot(n,normalize(vec3(0.9,-0.1,0.6))),0.0);float s=pow(max(dot(n,normalize(light+vec3(0.0,0.0,1.0))),0.0),38.0);gl_FragColor=vec4(color*(0.48+0.46*d+0.12*f)+vec3(0.1)*s,1.0);}'
    ];
    const program = gl.createProgram(), shaders = shaderSources.map((source, index) => {
      const shader = gl.createShader(index ? gl.FRAGMENT_SHADER : gl.VERTEX_SHADER);
      gl.shaderSource(shader, source); gl.compileShader(shader);
      if (!gl.getShaderParameter(shader, gl.COMPILE_STATUS)) throw Error('Shader unavailable');
      gl.attachShader(program, shader); return shader;
    });
    gl.linkProgram(program);
    if (!gl.getProgramParameter(program, gl.LINK_STATUS)) throw Error('Renderer unavailable');
    const locations = { position: gl.getAttribLocation(program, 'position'), normal: gl.getAttribLocation(program, 'normal') };
    for (const key of ['rotation', 'scale', 'aspect', 'color']) locations[key] = gl.getUniformLocation(program, key);
    let parts = [], halfExtents = [1, 1, 1], yaw = -0.18, pitch = 0.23, zoom = 1, active = true, request = 0;
    const events = new AbortController(), pointers = new Map(); let pinch = 0;
    const listen = (node, name, handler, options = {}) => node.addEventListener(name, handler, { ...options, signal: events.signal });
    const freeParts = () => { parts.forEach(part => Object.values(part.buffers).forEach(buffer => gl.deleteBuffer(buffer))); parts = []; };
    function draw() {
      request = 0;
      if (!active || !parts.length || gl.isContextLost()) return;
      const rect = canvas.getBoundingClientRect(), width = rect.width || canvas.width, height = rect.height || canvas.height;
      const dpr = Math.min(window.devicePixelRatio || 1, 2);
      const w = Math.min(1800, Math.round(width * dpr)), h = Math.min(1400, Math.round(height * dpr));
      if (!w || !h) return;
      if (canvas.width !== w || canvas.height !== h) { canvas.width = w; canvas.height = h; }
      const cy = Math.cos(yaw), sy = Math.sin(yaw), cp = Math.cos(pitch), sp = Math.sin(pitch);
      const rotation = new Float32Array([cy, sp * sy, -cp * sy, 0, cp, sp, sy, -sp * cy, cp * cy]);
      gl.viewport(0, 0, w, h); gl.clearColor(0, 0, 0, 0); gl.clear(gl.COLOR_BUFFER_BIT | gl.DEPTH_BUFFER_BIT);
      gl.enable(gl.DEPTH_TEST); gl.disable(gl.CULL_FACE); gl.useProgram(program);
      gl.uniformMatrix3fv(locations.rotation, false, rotation);
      const projectedX = Math.abs(rotation[0]) * halfExtents[0] + Math.abs(rotation[3]) * halfExtents[1] + Math.abs(rotation[6]) * halfExtents[2];
      const projectedY = Math.abs(rotation[1]) * halfExtents[0] + Math.abs(rotation[4]) * halfExtents[1] + Math.abs(rotation[7]) * halfExtents[2];
      const fit = 0.8 * Math.min((w / h) / Math.max(projectedX, 0.001), 1 / Math.max(projectedY, 0.001));
      gl.uniform1f(locations.aspect, w / h); gl.uniform1f(locations.scale, fit * zoom);
      for (const part of parts) {
        for (const key of ['position', 'normal']) {
          gl.bindBuffer(gl.ARRAY_BUFFER, part.buffers[key]); gl.enableVertexAttribArray(locations[key]); gl.vertexAttribPointer(locations[key], 3, gl.FLOAT, false, 0, 0);
        }
        gl.bindBuffer(gl.ELEMENT_ARRAY_BUFFER, part.buffers.index); gl.uniform3fv(locations.color, part.color);
        gl.drawElements(gl.TRIANGLES, part.count, part.type, 0);
      }
    }
    const render = () => { if (!request && active) request = requestAnimationFrame(draw); };
    const zoomBy = factor => { zoom = clamp(zoom * factor, 0.6, 3); render(); };
    const view = name => {
      if (name === 'front') { yaw = 0; pitch = 0; }
      else if (name === 'back') { yaw = Math.PI; pitch = 0; }
      else if (name === 'top') { yaw = 0; pitch = -Math.PI / 2; }
      else if (name === 'left') yaw -= Math.PI / 6;
      else if (name === 'right') yaw += Math.PI / 6;
      else { yaw = -0.18; pitch = 0.23; zoom = 1; }
      render();
    };
    listen(canvas, 'pointerdown', event => {
      if (event.pointerType === 'mouse' && event.button !== 0) return;
      pointers.set(event.pointerId, [event.clientX, event.clientY]); canvas.setPointerCapture(event.pointerId); canvas.focus({ preventScroll: true });
      if (pointers.size === 2) { const p = [...pointers.values()]; pinch = Math.hypot(p[0][0] - p[1][0], p[0][1] - p[1][1]); }
    });
    listen(canvas, 'pointermove', event => {
      const previous = pointers.get(event.pointerId); if (!previous) return;
      pointers.set(event.pointerId, [event.clientX, event.clientY]);
      if (pointers.size === 2) {
        const p = [...pointers.values()], distance = Math.hypot(p[0][0] - p[1][0], p[0][1] - p[1][1]);
        if (pinch) zoomBy(distance / pinch); pinch = distance;
      } else if (pointers.size === 1) { yaw += (event.clientX - previous[0]) * 0.009; pitch = clamp(pitch + (event.clientY - previous[1]) * 0.009, -Math.PI / 2, Math.PI / 2); render(); }
    });
    for (const name of ['pointerup', 'pointercancel', 'lostpointercapture']) listen(canvas, name, event => { pointers.delete(event.pointerId); pinch = 0; });
    listen(canvas, 'wheel', event => { event.preventDefault(); zoomBy(Math.exp(-event.deltaY * 0.0015)); }, { passive: false });
    listen(canvas, 'keydown', event => {
      if (!['ArrowLeft', 'ArrowRight', 'ArrowUp', 'ArrowDown', '+', '=', '-', 'Home'].includes(event.key)) return;
      event.preventDefault();
      if (event.key === '+' || event.key === '=') zoomBy(1.2);
      else if (event.key === '-') zoomBy(1 / 1.2);
      else if (event.key === 'Home') view('reset');
      else { yaw += event.key === 'ArrowLeft' ? -0.16 : event.key === 'ArrowRight' ? 0.16 : 0; pitch = clamp(pitch + (event.key === 'ArrowUp' ? -0.16 : event.key === 'ArrowDown' ? 0.16 : 0), -Math.PI / 2, Math.PI / 2); render(); }
    });
    listen(canvas, 'webglcontextlost', event => { event.preventDefault(); active = false; onError(); });
    const observer = new ResizeObserver(render); observer.observe(canvas);
    return {
      setScene(scene) {
        freeParts();
        const bounds = groupBounds(scene.instances, scene.models);
        const center = bounds.min.map((value, axis) => (value + bounds.max[axis]) / 2);
        const span = Math.max(...bounds.max.map((value, axis) => value - bounds.min[axis]));
        if (!(span > 0)) throw Error('Empty scene');
        halfExtents = bounds.max.map((value, axis) => (value - bounds.min[axis]) / span);
        for (const instance of scene.instances) for (const mesh of scene.models[instance.id].meshes) {
          const positions = decodeVectors(mesh), normals = decodeVectors(mesh, true);
          const indices = decode(mesh.indices, mesh.indexType === 'uint32' ? Uint32Array : Uint16Array);
          if (!positions.length || positions.length !== normals.length || !indices.length) throw Error('Invalid mesh');
          const type = indices instanceof Uint32Array ? gl.UNSIGNED_INT : gl.UNSIGNED_SHORT;
          if (type === gl.UNSIGNED_INT && !gl.getExtension('OES_element_index_uint')) throw Error('Unsupported mesh');
          for (let index = 0; index < positions.length; index++) {
            if (!Number.isFinite(positions[index]) || !Number.isFinite(normals[index])) throw Error('Invalid geometry');
            positions[index] = (positions[index] + instance.position[index % 3] - center[index % 3]) * 2 / span;
          }
          const buffers = { position: gl.createBuffer(), normal: gl.createBuffer(), index: gl.createBuffer() };
          for (const [key, array] of [['position', positions], ['normal', normals]]) { gl.bindBuffer(gl.ARRAY_BUFFER, buffers[key]); gl.bufferData(gl.ARRAY_BUFFER, array, gl.STATIC_DRAW); }
          gl.bindBuffer(gl.ELEMENT_ARRAY_BUFFER, buffers.index); gl.bufferData(gl.ELEMENT_ARRAY_BUFFER, indices, gl.STATIC_DRAW);
          const hex = /^#[0-9a-f]{6}$/i.test(mesh.color) ? mesh.color : '#b9babc';
          const color = new Float32Array([1, 3, 5].map(start => parseInt(hex.slice(start, start + 2), 16) / 255));
          parts.push({ buffers, color, count: indices.length, type });
        }
        view('reset');
      },
      view, zoomBy,
      snapshot() {
        if (!active || !parts.length || gl.isContextLost()) throw Error('Preview unavailable');
        draw();
        const image = document.createElement('canvas'); image.width = Math.min(900, canvas.width); image.height = Math.round(canvas.height * image.width / canvas.width);
        const context = image.getContext('2d'); context.fillStyle = '#f5f5f5'; context.fillRect(0, 0, image.width, image.height); context.drawImage(canvas, 0, 0, image.width, image.height);
        return image.toDataURL('image/png');
      },
      destroy() { active = false; cancelAnimationFrame(request); events.abort(); observer.disconnect(); freeParts(); shaders.forEach(shader => gl.deleteShader(shader)); gl.deleteProgram(program); gl.getExtension('WEBGL_lose_context')?.loseContext(); }
    };
  }
  async function renderToCanvas(canvas, state, assets, onError) {
    const scene = await buildScene(state, assets), renderer = createRenderer(canvas, onError);
    try { renderer.setScene(scene); return renderer; } catch (error) { renderer.destroy(); throw error; }
  }
  function isConfirmed(form) {
    try { return confirmations.get(form)?.signature === signature(stateFromForm(form)); } catch (_) { return false; }
  }
  function replaceVisuals(form, poster, text) {
    const card = form.closest('[data-mrf-card]'), root = form.closest('[data-mrf-root]');
    if (!originalVisuals.has(form)) {
      const images = [...card.querySelectorAll('.mrf-card__image img')];
      const galleryImage = root.matches('.mrf-product-page') ? root.querySelector('.mrf-product-gallery figure img') : null;
      if (galleryImage) images.push(galleryImage);
      const captions = [...card.querySelectorAll('.mrf-card__visual-note')];
      const galleryCaption = root.matches('.mrf-product-page') ? root.querySelector('.mrf-product-gallery .mrf-caption') : null;
      if (galleryCaption) captions.push(galleryCaption);
      originalVisuals.set(form, { images: images.map(node => ({ node, attributes: Object.fromEntries(['src', 'srcset', 'sizes', 'alt'].map(key => [key, node.getAttribute(key)])) })), captions: captions.map(node => ({ node, text: node.textContent })) });
    }
    const originals = originalVisuals.get(form);
    originals.images.forEach(({ node }) => { node.removeAttribute('srcset'); node.removeAttribute('sizes'); node.src = poster; node.alt = text.image_label; node.dataset.mrfConfirmedImage = 'true'; });
    originals.captions.forEach(({ node }) => { node.textContent = text.confirmed_caption; });
  }
  function invalidate(form) {
    if (!confirmations.has(form)) return;
    confirmations.delete(form);
    const property = form.querySelector('[data-mrf-composition-property]'); if (property) property.value = '';
    const originals = originalVisuals.get(form);
    if (originals) {
      originals.images.forEach(({ node, attributes }) => { Object.entries(attributes).forEach(([key, value]) => { if (value === null) node.removeAttribute(key); else node.setAttribute(key, value); }); delete node.dataset.mrfConfirmedImage; });
      originals.captions.forEach(({ node, text }) => { node.textContent = text; }); originalVisuals.delete(form);
    }
    const preview = form.querySelector('[data-mrf-composition-preview]'); if (preview) preview.hidden = true;
    form.dispatchEvent(new CustomEvent('mrf:composition-invalidated', { bubbles: true }));
  }
  function init(root) {
    if (!root || dialogs.has(root)) return;
    const dialog = root.querySelector('[data-mrf-composition-dialog]'); if (!dialog) return;
    const config = JSON.parse(dialog.querySelector('[data-mrf-composition-config]').textContent), text = config.text;
    const status = dialog.querySelector('[data-mrf-composition-status]'), stage = dialog.querySelector('[data-mrf-composition-stage]');
    const controls = dialog.querySelector('[data-mrf-composition-controls]'), confirm = dialog.querySelector('[data-mrf-composition-confirm]');
    let renderer, canvas, generation = 0, opener, form, state, stateKey, previousOverflow;
    const failure = () => { dialog.dataset.state = 'error'; status.hidden = false; status.textContent = text.error; controls.hidden = true; confirm.disabled = false; if (canvas) canvas.hidden = true; };
    const names = state => [
      ...(state.kit !== 'outils5' ? [config.names.doigt] : []), ...state.nails.map(key => config.names[nailModels[key]]), ...state.files.map(key => config.names['lime-' + key]),
      ...state.tools.map(key => config.names[key]), ...(state.kit !== 'essentiel' ? [config.names.presentoir5] : [])
    ];
    const open = async (sourceForm, trigger) => {
      try { state = stateFromForm(sourceForm); } catch (_) { return false; }
      form = sourceForm; opener = trigger; stateKey = signature(state);
      const current = ++generation;
      renderer?.destroy(); renderer = null; canvas?.remove(); canvas = null;
      dialog.dataset.state = 'loading'; status.textContent = text.loading; status.hidden = false; controls.hidden = true; confirm.disabled = false;
      dialog.querySelector('[data-mrf-composition-title]').textContent = sourceForm.closest('[data-mrf-card]').querySelector('h3')?.textContent || text.title;
      dialog.querySelector('[data-mrf-composition-size]').textContent = text.size.replace('{{ size }}', state.size);
      dialog.querySelector('[data-mrf-composition-price]').textContent = sourceForm.closest('[data-mrf-card]').querySelector('[data-mrf-price]')?.textContent || '';
      const list = dialog.querySelector('[data-mrf-composition-list]'); list.replaceChildren();
      names(state).forEach(name => { const item = document.createElement('li'); item.textContent = name; list.append(item); });
      if (!dialog.open) { previousOverflow = document.body.style.overflow; document.body.style.overflow = 'hidden'; dialog.showModal(); }
      try {
        const scene = await buildScene(state, config.assets);
        if (current !== generation || !dialog.open) return false;
        canvas = document.createElement('canvas'); canvas.tabIndex = 0; canvas.setAttribute('role', 'img'); canvas.setAttribute('aria-label', text.canvas_label); stage.append(canvas);
        renderer = createRenderer(canvas, failure); renderer.setScene(scene);
        dialog.dataset.state = 'ready'; status.hidden = true; controls.hidden = false; confirm.disabled = false;
        const spacingNote = dialog.querySelector('[data-mrf-stand-spacing-note]');
        if (spacingNote) spacingNote.hidden = !scene.standSpacingUnconfirmed;
        try {
          if (signature(stateFromForm(form)) === stateKey) {
            const poster = renderer.snapshot();
            const detail = { state, signature: stateKey, poster, pngDataURL: poster, variantId: form.querySelector('[data-mrf-effective-variant]')?.value || form.querySelector('[data-mrf-variant]:checked')?.value };
            confirmations.set(form, detail);
            const property = form.querySelector('[data-mrf-composition-property]'); if (property) property.value = stateKey;
            replaceVisuals(form, poster, text);
            const preview = form.querySelector('[data-mrf-composition-preview]');
            if (preview) { const image = preview.querySelector('img'); image.src = poster; image.alt = text.image_label; preview.hidden = false; }
            form.dispatchEvent(new CustomEvent('mrf:composition-confirmed', { bubbles: true, detail }));
          }
        } catch (_) { invalidate(form); }
        return true;
      } catch (_) { if (current === generation && dialog.open) failure(); return false; }
    };
    dialogs.set(root, { open, assets: config.assets });
    root.querySelectorAll('[data-mrf-composition-open]').forEach(button => {
      const targetForm = button.closest('form');
      const hintId = button.getAttribute('aria-describedby');
      const hint = hintId ? document.getElementById(hintId) : null;
      const refresh = () => {
        try { stateFromForm(targetForm); button.disabled = false; } catch (_) { button.disabled = true; }
        if (hint) {
          hint.hidden = !button.disabled;
          if (button.disabled) button.setAttribute('aria-describedby', hintId);
          else button.removeAttribute('aria-describedby');
        }
        if (!isConfirmed(targetForm)) invalidate(targetForm);
      };
      targetForm.addEventListener('change', refresh);
      root.querySelectorAll('[data-mrf-global-size]').forEach(input => input.addEventListener('change', () => queueMicrotask(refresh)));
      button.addEventListener('click', () => open(targetForm, button)); refresh();
    });
    confirm.addEventListener('click', () => dialog.close());
    dialog.querySelector('[data-mrf-composition-close]').addEventListener('click', () => dialog.close());
    dialog.querySelector('[data-mrf-composition-edit]').addEventListener('click', () => dialog.close());
    dialog.querySelector('[data-mrf-composition-retry]').addEventListener('click', () => open(form, opener));
    dialog.querySelectorAll('[data-mrf-composition-view]').forEach(button => button.addEventListener('click', () => renderer?.view(button.dataset.mrfCompositionView)));
    dialog.querySelectorAll('[data-mrf-composition-zoom]').forEach(button => button.addEventListener('click', () => renderer?.zoomBy(Number(button.dataset.mrfCompositionZoom))));
    dialog.addEventListener('close', () => { generation++; renderer?.destroy(); renderer = null; canvas?.remove(); canvas = null; document.body.style.overflow = previousOverflow || ''; opener?.focus({ preventScroll: true }); });
  }
  window.YvarMrfComposition = Object.freeze({ normalize, signature, stateFromForm, buildScene, renderToCanvas, isConfirmed, invalidate,
    confirmed(form) { const record = confirmations.get(form); return isConfirmed(form) && record ? { ...record, state: normalize(record.state) } : null; },
    open(form, trigger) { const root = form.closest('[data-mrf-root]'); init(root); return dialogs.get(root)?.open(form, trigger); },
    assets(root) { return dialogs.get(root)?.assets; }
  });
  const initAll = () => document.querySelectorAll('[data-mrf-root]').forEach(init);
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', initAll); else initAll();
  document.addEventListener('shopify:section:load', initAll);
})();


